import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { MeRes, SessionUser, Role } from "@etheria/shared";
import { api, ApiError } from "./api";

/**
 * AuthProvider / useAuth — session résolue via `GET /api/auth/me` (SITE_SPEC §2).
 *
 * Le rôle (`ADMIN`/`MODO`/`STAFF`/`JOUEUR`) est dérivé côté serveur du rang du joueur lié
 * (`etheria_player_ranks`) — jamais fourni par le client. Le hook expose des helpers de
 * gating (`isAuthenticated`, `isAdmin`, `hasRole`) consommés par la nav et les routes gardées.
 */

interface AuthState {
  user: SessionUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isLinked: boolean;
  isAdmin: boolean;
  isStaff: boolean;
  role: Role;
  hasRole: (min: Role) => boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const ME_KEY = ["auth", "me"] as const;

/** Poids des rôles pour le gating hiérarchique. */
const ROLE_WEIGHT: Record<Role, number> = { JOUEUR: 0, STAFF: 1, MODO: 2, ADMIN: 3 };

async function fetchMe(signal?: AbortSignal): Promise<MeRes> {
  try {
    return await api.get<MeRes>("/auth/me", { signal });
  } catch (e) {
    // Non authentifié → session anonyme, pas une erreur bloquante.
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      return { authenticated: false, user: null };
    }
    throw e;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ME_KEY,
    queryFn: ({ signal }) => fetchMe(signal),
    staleTime: 60_000,
    retry: false,
  });

  const value = useMemo<AuthState>(() => {
    const user = data?.user ?? null;
    const role: Role = user?.role ?? "JOUEUR";
    return {
      user,
      isLoading,
      isAuthenticated: Boolean(data?.authenticated && user),
      isLinked: Boolean(user?.linked),
      isAdmin: Boolean(user?.admin),
      isStaff: ROLE_WEIGHT[role] >= ROLE_WEIGHT.STAFF && Boolean(user),
      role,
      hasRole: (min: Role) => Boolean(user) && ROLE_WEIGHT[role] >= ROLE_WEIGHT[min],
      refresh: async () => {
        await qc.invalidateQueries({ queryKey: ME_KEY });
      },
      logout: async () => {
        try {
          await api.post("/auth/logout");
        } finally {
          qc.setQueryData<MeRes>(ME_KEY, { authenticated: false, user: null });
          await qc.invalidateQueries({ queryKey: ME_KEY });
        }
      },
    };
  }, [data, isLoading, qc]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth doit être utilisé dans un <AuthProvider>.");
  return ctx;
}
